import { GIFTS, KERB_LANE, LANES, POWER_UPS, SCORE_STARS, type GiftKind, type PowerUpId } from '@accra/shared';
import type { Rng } from '../../core/rng';
import type { Pickup, RunState } from '../../game/RunState';
import { laneX } from '../lanes';
import { nextJunction } from '../junctions/signals';
import { stopAt } from '../passengers/stopSequence';

const SPAWN_AHEAD = 170;
const DESPAWN_BEHIND = 15;
const COIN_GAP = 3;
const COIN_HEIGHT = 1.1;

function weighted<T extends { weight: number }>(rng: Rng, items: readonly T[]): T {
  let roll = rng.range(0, items.reduce((sum, i) => sum + i.weight, 0));
  for (const item of items) {
    roll -= item.weight;
    if (roll <= 0) return item;
  }
  return items[items.length - 1] as T;
}

const GIFT_LIST = (Object.keys(GIFTS) as GiftKind[]).map((kind) => ({ kind, weight: GIFTS[kind].weight }));

/** Rows of pesewa coins, a power-up crate every few hundred metres, score stars (×2 common, ×4 rare) and gifts. */
export class PickupSpawner {
  private nextCoinsAt = 90;
  private nextCrateAt = 260;
  private nextStarAt = 180;
  private nextGiftAt = 420;
  private nextId = 1;

  constructor(private readonly rng: Rng) {}

  update(state: RunState): void {
    const horizon = state.bus.s + SPAWN_AHEAD;
    while (this.nextCoinsAt < horizon) {
      this.coinRow(state, this.nextCoinsAt);
      this.nextCoinsAt += this.rng.range(60, 110);
    }
    while (this.nextCrateAt < horizon) {
      this.crate(state, this.nextCrateAt);
      this.nextCrateAt += this.rng.range(220, 360);
    }
    while (this.nextStarAt < horizon) {
      this.special(state, this.nextStarAt, 'star');
      this.nextStarAt += this.rng.range(320, 560);
    }
    while (this.nextGiftAt < horizon) {
      this.special(state, this.nextGiftAt, 'gift');
      this.nextGiftAt += this.rng.range(380, 640);
    }
    state.pickups = state.pickups.filter((p) => p.s > state.bus.s - DESPAWN_BEHIND);
  }

  /** Keep the stop box lane and the junction box clear. */
  private blocked(state: RunState, s: number, lane: number): boolean {
    const junction = nextJunction(s - 20);
    if (Math.abs(junction.position - s) < 20) return true;
    for (let seq = state.stop.seq; seq < state.stop.seq + 2; seq += 1) {
      if (lane === KERB_LANE && Math.abs(stopAt(seq).position - s) < 40) return true;
    }
    return false;
  }

  private coinRow(state: RunState, at: number): void {
    const lane = this.rng.int(0, LANES - 1);
    const count = this.rng.int(6, 9);
    for (let i = 0; i < count; i += 1) {
      const s = at + i * COIN_GAP;
      if (this.blocked(state, s, lane)) continue;
      state.pickups.push(this.make('coin', null, s, lane, COIN_HEIGHT));
    }
  }

  private crate(state: RunState, at: number): void {
    const lane = this.rng.int(0, LANES - 1);
    if (this.blocked(state, at, lane)) return;
    const power = this.rng.pick(POWER_UPS).id;
    state.pickups.push(this.make('crate', power, at, lane, 1.2));
  }

  private special(state: RunState, at: number, kind: 'star' | 'gift'): void {
    const lane = this.rng.int(0, LANES - 1);
    if (this.blocked(state, at, lane)) return;
    const pickup = this.make(kind, null, at, lane, kind === 'star' ? 1.5 : 0.7);
    if (kind === 'star') pickup.factor = weighted(this.rng, SCORE_STARS).factor;
    else pickup.gift = weighted(this.rng, GIFT_LIST).kind;
    state.pickups.push(pickup);
  }

  private make(kind: Pickup['kind'], power: PowerUpId | null, s: number, lane: number, y: number): Pickup {
    return { id: this.nextId++, kind, power, s, x: laneX(lane), y };
  }
}
